import React, { useState, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

// Menü öğesi türleri
interface MenuItem {
  to?: string;
  label: string;
  icon?: React.ReactNode;
  resource?: string;
  resources?: string[];
  children?: MenuItem[];
  action?: string;
  className?: string;
}

// Alt menü bileşeni
const SubMenu: React.FC<{
  item: MenuItem;
  isOpen: boolean;
  toggleSubmenu: () => void;
  isSidebarOpen: boolean;
  isMobile: boolean;
  isActive: boolean;
}> = ({ item, isOpen, toggleSubmenu, isSidebarOpen, isMobile, isActive }) => {
  const { hasPermission, isAdmin } = useContext(AuthContext);
  const location = useLocation();

  // Alt menü öğelerinden en az birine erişim var mı?
  const hasAccessToAnyChild = isAdmin || (item.children?.some(child => {
    if (child.resources && child.resources.length > 0) {
      return child.resources.some(res => hasPermission(res, child.action || 'read'));
    }
    return !child.resource || hasPermission(child.resource, child.action || 'read');
  }) || false);

  // Eğer hiçbir alt menü öğesine erişim yoksa, menüyü tamamen gizle
  if (!hasAccessToAnyChild) {
    return null;
  }

  return (
    <div className="mb-1">
      {/* Ana menü başlığı */}
      <button
        onClick={toggleSubmenu}
        className={`w-full flex items-center px-4 py-2 rounded-md transition-colors ${
          isActive
            ? 'bg-blue-50 text-blue-600'
            : 'text-gray-700 hover:bg-gray-100'
        }`}
      >
        <div className={isSidebarOpen || isMobile ? 'mr-3' : ''}>{item.icon}</div>
        
        {(isSidebarOpen || isMobile) && (
          <>
            <span className="flex-1 text-left">{item.label}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {/* Alt menü öğeleri - açıksa göster */}
      {(isOpen && (isSidebarOpen || isMobile)) && (
        <div className="pl-10 mt-1 space-y-1">
          {item.children?.map((child, idx) => {
            // Alt menü öğesi için erişim kontrolü
            const hasChildAccess = isAdmin || 
              (child.resources?.some(res => hasPermission(res, child.action || 'read'))) ||
              (!child.resource || hasPermission(child.resource, child.action || 'read'));
            
            if (!hasChildAccess) return null;
            
            const childIsActive = location.pathname === child.to || 
                                (child.to && child.to !== '/' && location.pathname.startsWith(child.to));

            return (
              <Link
                key={idx}
                to={child.to || '#'}
                className={`block py-2 pl-3 rounded-md text-sm ${
                  childIsActive
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {child.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

// NavLink bileşeni - tek bir menü öğesi için
const NavLink: React.FC<{
  item: MenuItem;
  isSidebarOpen: boolean;
  isMobile: boolean;
}> = ({ item, isSidebarOpen, isMobile }) => {
  const location = useLocation();
  const { hasPermission, isAdmin } = useContext(AuthContext);
  const isActive = location.pathname === item.to || 
                  (item.to && item.to !== '/' && location.pathname.startsWith(item.to));
  
  // Erişim kontrolü
  const hasAccess = isAdmin || 
    (item.resources?.some(res => hasPermission(res, item.action || 'read'))) ||
    (!item.resource || hasPermission(item.resource, item.action || 'read'));
  
  if (!hasAccess) return null;
  
  return (
    <Link
      to={item.to || '#'}
      className={`flex items-center px-4 py-2 rounded-md ${
        isActive ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:bg-gray-100'
      } ${!isSidebarOpen && !isMobile ? 'justify-center' : ''}`}
    >
      <div className={isSidebarOpen || isMobile ? 'mr-3' : ''}>{item.icon}</div>
      {(isSidebarOpen || isMobile) && <span>{item.label}</span>}
    </Link>
  );
};

interface SidebarProps {
  sidebarOpen: boolean;
  isMobile: boolean;
  toggleSidebar: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ sidebarOpen, isMobile, toggleSidebar }) => {
  const { hasPermission, isAdmin } = useContext(AuthContext);
  const location = useLocation();
  
  // Alt menülerin açık/kapalı durumlarını tutacak state
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({
    design: false,
    sourcing: false,
    modelhane: false,
    planlama: false
  });

  // Alt menü açma/kapama fonksiyonu
  const toggleSubmenu = (key: string) => {
    setOpenSubmenus(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Ana menü öğeleri - yeni yapılandırma
  const menuItems: MenuItem[] = [
    {
      to: '/',
      label: 'Ana Sayfa',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      // Herkes erişebilir
    },
    {
      label: "Tasarım",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      ),
      resources: ['products', 'designer'],
      children: [

        {
          to: "/technical-sheet",
          label: "Teknik Föyler",
          resource: "designer"
        },
        {
          to: "/products?tab=prototypes",
          label: "Ürünler",
          resource: "products"
        }
      ]
    },
    {
      label: "Sourcing",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
      resources: ['fabrics', 'accessories', 'colors', 'sourcing'],
      children: [
        {
          to: "/fabrics",
          label: "Kumaşlar",
          resource: "fabrics"
        },
        {
          to: "/accessories",
          label: "Aksesuarlar",
          resource: "accessories"
        },
        {
          to: "/colors",
          label: "Renkler",
          resource: "colors"
        },
        {
          to: "/materials",
          label: "Malzeme Listesi",
          resource: "sourcing"
        },
        {
          to: "/vendors",
          label: "Üretici havuzu",
          resource: "vendors"
        }
      ]
    },
    {
      label: "Modelhane",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      resources: ['modelist', 'prototype'],
      children: [
        /*{
          to: "/olculer",
          label: "Ölçüler",
          resource: "modelist"
        },*/
        {
          to: "/products",
          label: "Prototip Ürünler",
          resource: "products"
        },{
          to: "/fit",
          label: "Fit Kritik",
          resource: "fitcrt"
        }
      ]
    },
    {
      label: "Planlama",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      resources: ['planlama', 'production'],
      children: [
        {
          to: "/uretim-planlama",
          label: "Üretim Planlaması",
          resource: "planlama"
        },
        {
          to: "/kapasite-planlama",
          label: "Kapasite Planlaması",
          resource: "planlama"
        },
        {
          to: "/terminler",
          label: "Terminler",
          resource: "planlama"
        },
        {
          to: "/siparis-takip",
          label: "Sipariş Takibi",
          resource: "production"
        }
      ]
    },
    {
      to: '/barcode-generator',
      label: 'Barkod Oluşturucu',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1v-2a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1z" />
        </svg>
      ),
      // Herkes erişebilir
    },
    {
      to: '/print-barcodes',
      label: 'Barkod Yazdır',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
        </svg>
      ),
      // Herkes erişebilir
    }
  ];

  // Admin menüsü
  const adminMenu: MenuItem[] = [
    {
      to: '/manage-users',
      label: 'Kullanıcı Yönetimi',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      resource: 'admin',
    },
    {
      to: '/roles',
      label: 'Roller',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      resource: 'admin',
    },
    {
      to: '/user-roles',
      label: 'Kullanıcı Rolleri',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      resource: 'admin',
    },
  ];

  return (
    <div
      className={`bg-white shadow-lg z-30 transition-all duration-300 
      ${isMobile ? `fixed top-16 bottom-0 left-0 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}` : `relative top-0 ${sidebarOpen ? 'w-64' : 'w-20'}`}`}
    >
      <div className="flex flex-col h-full">
        {/* Mobile close button */}
        {isMobile && (
          <div className="p-4 border-b border-gray-200">
            <button onClick={toggleSidebar} className="text-gray-500 hover:text-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        
        {/* Main Menu */}
        <div className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {/* Ana sayfa - düz bağlantı */}
          <NavLink item={menuItems[0]} isSidebarOpen={sidebarOpen} isMobile={isMobile} />
          
          {/* Kategori menüleri - açılır kapanır */}
          <SubMenu
            key="design"
            item={menuItems[1]}
            isOpen={openSubmenus.design}
            toggleSubmenu={() => toggleSubmenu('design')}
            isSidebarOpen={sidebarOpen}
            isMobile={isMobile}
            isActive={location.pathname.includes('/dizayn') || 
                    location.pathname.includes('/bedenler') || 
                    location.pathname.includes('/teknik-foy') ||
                    location.pathname.includes('/products')}
          />
          
          <SubMenu
            key="sourcing"
            item={menuItems[2]}
            isOpen={openSubmenus.sourcing}
            toggleSubmenu={() => toggleSubmenu('sourcing')}
            isSidebarOpen={sidebarOpen}
            isMobile={isMobile}
            isActive={location.pathname.includes('/fabrics') || 
                    location.pathname.includes('/accessories') || 
                    location.pathname.includes('/colors') || 
                    location.pathname.includes('/materials') || 
                    location.pathname.includes('/vendors') || 
                    location.pathname.includes('/orders')}
          />
          
          <SubMenu
            key="modelhane"
            item={menuItems[3]}
            isOpen={openSubmenus.modelhane}
            toggleSubmenu={() => toggleSubmenu('modelhane')}
            isSidebarOpen={sidebarOpen}
            isMobile={isMobile}
            isActive={location.pathname.includes('/olculer') || 
                    location.pathname.includes('/prototype-products')}
          />
          
          <SubMenu
            key="planlama"
            item={menuItems[4]}
            isOpen={openSubmenus.planlama}
            toggleSubmenu={() => toggleSubmenu('planlama')}
            isSidebarOpen={sidebarOpen}
            isMobile={isMobile}
            isActive={location.pathname.includes('/uretim-planlama') || 
                   location.pathname.includes('/kapasite-planlama') || 
                   location.pathname.includes('/terminler') ||
                   location.pathname.includes('/siparis-takip')}
          />
          
          {/* Barkod işlemleri - düz bağlantılar */}
          <NavLink item={menuItems[5]} isSidebarOpen={sidebarOpen} isMobile={isMobile} />
          <NavLink item={menuItems[6]} isSidebarOpen={sidebarOpen} isMobile={isMobile} />
          
          {/* Admin Menu Bölümü */}
          {adminMenu.some(item => isAdmin || hasPermission(item.resource || '', 'read')) && (
            <>
              <div className={`my-2 border-t border-gray-200 pt-2 ${sidebarOpen ? 'mx-4' : 'mx-2'}`}>
                {sidebarOpen && <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-2 mb-1 ml-4">Admin</h3>}
              </div>
              {adminMenu.map((item, idx) => (
                <NavLink
                  key={`admin-${idx}`}
                  item={{
                    ...item,
                    className: "text-orange-600 hover:bg-orange-50"
                  }}
                  isSidebarOpen={sidebarOpen}
                  isMobile={isMobile}
                />
              ))}
            </>
          )}
        </div>
        
        {/* Toggle sidebar button (only on desktop) */}
        {!isMobile && (
          <div className="p-4 border-t border-gray-200 flex justify-center">
            <button
              onClick={toggleSidebar}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              {sidebarOpen ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;